import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { SERVICE_OPTIONS, GALLERY } from "@/data/content";
import { Reveal } from "./Reveal";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EMPTY = {
  first_name: "", last_name: "", email: "", phone: "",
  service_address: "", service_type: "", project_description: "",
  preferred_start_date: "",
};

const Field = ({ label, name, type = "text", value, onChange, placeholder, required }) => (
  <div className="flex flex-col gap-2">
    <label htmlFor={name} className="text-xs uppercase tracking-[0.15em] text-cream/60">
      {label}{required && <span className="text-gold"> *</span>}
    </label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      data-testid={`quote-${name}`}
      className="bg-transparent border-b border-gold/40 focus:border-gold text-cream placeholder-cream/30 py-3 outline-none transition-colors"
    />
  </div>
);

export const QuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_type) {
      toast.error("Please select a service type.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/quotes", form);
      setDone(true);
      setForm(EMPTY);
      toast.success("Your request has been received. We'll be in touch shortly!");
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="relative py-24 sm:py-32 overflow-hidden bg-ink grain"
    >
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25"
        style={{ backgroundImage: `url(${GALLERY[5].url})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/90 to-ink" />

      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12">
        <Reveal className="text-center mb-14">
          <p className="text-sm uppercase tracking-[0.3em] text-gold font-semibold mb-4">
            Request a Free Quote
          </p>
          <h2 className="font-display text-cream text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
            Let's Build Your Sanctuary
          </h2>
          <p className="mt-5 text-cream/60 max-w-xl mx-auto">
            Tell us about your project and our team will prepare a tailored,
            no-obligation quote within one business day.
          </p>
        </Reveal>

        {done ? (
          <Reveal>
            <div data-testid="quote-success" className="glass-dark p-12 text-center">
              <CheckCircle2 className="text-gold mx-auto mb-6" size={54} />
              <h3 className="font-display text-2xl text-cream mb-3">Thank You</h3>
              <p className="text-cream/70">
                Your request has been received. A Canopy Grounds specialist will
                reach out shortly to discuss your project.
              </p>
              <button
                onClick={() => setDone(false)}
                data-testid="quote-another-btn"
                className="mt-8 border border-gold text-gold px-8 py-3 text-sm uppercase tracking-[0.15em] hover:bg-gold/10 transition-colors"
              >
                Submit Another
              </button>
            </div>
          </Reveal>
        ) : (
          <Reveal>
            <form onSubmit={submit} data-testid="quote-form" className="glass-dark p-8 sm:p-12 space-y-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label="First Name" name="first_name" value={form.first_name} onChange={onChange} placeholder="Jane" required />
                <Field label="Last Name" name="last_name" value={form.last_name} onChange={onChange} placeholder="Doe" required />
                <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} placeholder="jane@email.com" required />
                <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={onChange} placeholder="(519) 555-0100" required />
              </div>

              <Field label="Service Address" name="service_address" value={form.service_address} onChange={onChange} placeholder="123 Maple Ave, Brantford, ON" required />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex flex-col gap-2">
                  <label className="text-xs uppercase tracking-[0.15em] text-cream/60">
                    Service Type <span className="text-gold">*</span>
                  </label>
                  <Select value={form.service_type} onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))}>
                    <SelectTrigger
                      data-testid="quote-service_type"
                      className="bg-transparent border-0 border-b border-gold/40 rounded-none text-cream px-0 focus:ring-0 focus:border-gold data-[placeholder]:text-cream/30"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-ink border-gold/30 text-cream">
                      {SERVICE_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt} data-testid={`service-option-${opt}`} className="focus:bg-forest focus:text-cream">
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Field label="Preferred Start Date" name="preferred_start_date" type="date" value={form.preferred_start_date} onChange={onChange} required />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="project_description" className="text-xs uppercase tracking-[0.15em] text-cream/60">
                  Project Description <span className="text-gold">*</span>
                </label>
                <textarea
                  id="project_description"
                  name="project_description"
                  rows={4}
                  required
                  value={form.project_description}
                  onChange={onChange}
                  data-testid="quote-project_description"
                  placeholder="Tell us about your vision, property size, and any specific needs…"
                  className="bg-transparent border border-gold/40 focus:border-gold text-cream placeholder-cream/30 p-4 outline-none transition-colors resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="quote-submit-button"
                className="w-full bg-gold text-ink py-4 text-sm font-semibold uppercase tracking-[0.2em] hover:bg-gold-hover transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? <><Loader2 className="animate-spin" size={18} /> Sending…</> : "Request My Free Quote"}
              </button>
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
};
